/**
 * [IMPORTANT NOTICE]
 * ANY CHANGES TO THIS FILE WITHOUT PROPER UNDERSTANDING MAY LEAD TO UNINTENDED BEHAVIOUR
 */

import { EventbriteHeader, Stage, stageFromString } from '@eventbrite/webapp-constants';
import { getDefaults, LOCALE_DEFAULTS } from '@eventbrite/webapp-locales';
import { IncomingMessage } from 'http';
import { GetServerSidePropsContext, NextPageContext } from 'next';
import { envConfig } from '../configs/envConfig';
import { mlpCustomHeaders, mlpCustomHeaderValues } from '../constants';
import { TLD_TO_LOCALE } from '../constants/global';
import { Env, User } from '../types';

/**
 * Returns Stage value
 */
export const getStage = () => {
  const stage = stageFromString(envConfig.STAGE || Stage.PROD);
  return stage;
};

/**
 * Returns the complete multi Slug path as single String
 */
export const getSlugString = (slug: string | Array<string>) => {
  return Array.isArray(slug) ? slug.join('/') : slug;
};

/**
 * Returns User Data Object with identifiers
 */
export const getUserDataFromContext = (context: GetServerSidePropsContext): User => {
  const { req } = context;

  const userId = (req.headers[EventbriteHeader.EB_USER_ID] as string) ?? '';
  const isUserLoggedIn = Boolean(userId);

  return {
    userId,
    soaToken: (req.headers[EventbriteHeader.EB_SOA_TOKEN] as string) ?? '',
    organizationId: (req.headers[EventbriteHeader.EB_ORGANIZATION_ID] as string) ?? '',
    guestId: (req.headers[EventbriteHeader.EB_GUEST_ID] as string) ?? '',
    correlationId: (req.headers[EventbriteHeader.EB_CORRELATION_ID] as string) ?? '',
    stableId: (req.headers[EventbriteHeader.EB_STABLE_ID] as string) ?? '',
    sessionId: (req.headers[EventbriteHeader.EB_SESSION_ID] as string) ?? '',
    isUserLoggedIn,
  };
};

/**
 * Uses Regex to extract TLD from ebHost string
 */
const getTldFromEBHost = (ebHost: string) => {
  return (ebHost.match(/(?:evbqa|evbstage|eventbrite)(\.\S+)$/) || [])[1];
};

/**
 * Fallback to .com (en_US), incase ebHost Header is missing/empty in request
 * This is necessary, as ebHost cannot be empty for apps to work
 */
const getFallbackEnv = (pagePath: string, ebLocale: string, slug: string): Env => {
  const ebHost = 'www.eventbrite.com';
  const locale = 'en_US';
  const tld = '.com';
  const serverUrl = `https://${ebHost}${pagePath}`;

  return {
    ebHost,
    stage: getStage(),
    pagePath,
    tld,
    locale,
    ebLocale,
    localeInfo: getDefaults(locale),
    serverUrl,
    slug,
  };
};

/**
 * Returns Env Data Object with request & server info
 */
export const getEnvDataFromContext = (
  context: GetServerSidePropsContext | NextPageContext,
): Env => {
  const { req } = context;
  const params = 'params' in context ? context.params : undefined;

  // ebLocale used for lang directory like /fr_CA/organizer/* etc
  const ebLocale = (req?.headers[mlpCustomHeaders.LOCALE] as string) || '';

  const slug = getSlugString(params?.slug || '');
  const pagePath = req?.url ?? '';

  const ebHost = (req?.headers[EventbriteHeader.EB_HOST] as string) || '';
  const serverUrl = `https://${ebHost}${pagePath}`;
  const tld = getTldFromEBHost(ebHost);

  const locale = ebLocale || TLD_TO_LOCALE[tld] || 'en_US';
  const localeInfo = getDefaults(locale);

  if (!ebHost) {
    // Fallback to .com in case of missing ebHost
    return getFallbackEnv(pagePath, ebLocale, slug);
  }

  return {
    ebHost,
    stage: getStage(),
    pagePath,
    tld,
    locale,
    ebLocale,
    localeInfo,
    serverUrl,
    slug,
  };
};

/**
 * This is for back compatibility for non domain environmentapps
 */
export const getHostnameFromStageAndTLD = (stage: string, tld: string) => {
  switch (stage) {
    case Stage.LOCAL:
    case Stage.DEV:
      return `https://dev.evbqa${tld}:4000/mlp-cms`;
    case Stage.TEST:
      return `https://www.evbqa${tld}`;
    case Stage.PROD:
    default:
      return `https://www.eventbrite${tld}`;
  }
};

/**
 * Returns the language-subdirectory prefix (e.g. `/fr_CA`) for locales that
 * serve under one, or an empty string otherwise. Prefix root-relative links
 * with this so they preserve the current locale; without it the edge routes
 * them to the TLD default locale instead.
 */
export const getLocaleSubdirectoryPrefix = (localeInfo?: Env['localeInfo']): string =>
  localeInfo?.uses_language_subdirectory ? `/${localeInfo.locale}` : '';

/**
 * Provides the site url based on locale
 * this is required for Hreflang formation based on locales
 * It handles language directories as well, eg. /fr_CA/
 */
export const getSiteUrlForLocale = (locale: string) => {
  const stage = getStage();
  let baseUri = stage == Stage.PROD ? 'https://www.eventbrite' : 'https://www.evbqa';

  const localeDefault = LOCALE_DEFAULTS.find((obj) => obj.locale == locale);
  if (!localeDefault) return null;

  if (localeDefault.uses_language_subdirectory) {
    return `${baseUri}${localeDefault.tld}/${localeDefault.locale}`;
  }
  return `${baseUri}${localeDefault.tld}`;
};

/**
 * check if this is Page preview or Webapp Preview request based on custom cookie
 */
export function isPagePreviewRequest(req: IncomingMessage, isPreviewParam: boolean): boolean {
  if (isPreviewParam) return true;

  const cookieHeader = req.headers.cookie;
  if (!cookieHeader) return false;

  const cookies = cookieHeader.split(';').reduce<Record<string, string>>((acc, cookie) => {
    const [key, value] = cookie.trim().split('=');
    acc[key] = value;
    return acc;
  }, {});

  // Check if MLP_WEBAPP cookie key exists
  if (mlpCustomHeaderValues.MLP_WEBAPP in cookies) {
    // Cookie key exists - return true if value is not CACHE_ME
    return cookies[mlpCustomHeaderValues.MLP_WEBAPP] !== mlpCustomHeaderValues.CACHE_ME;
  }

  return false;
}
